(() => {
  if (customElements.get('gvs-product-list-carousel')) return;

  class GVSProductListCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gvs-product-track]');
      this.slides = Array.from(this.querySelectorAll('[data-gvs-product-slide]'));
      this.previousButton = this.querySelector('[data-gvs-product-prev]');
      this.nextButton = this.querySelector('[data-gvs-product-next]');
      this.dots = Array.from(this.querySelectorAll('[data-gvs-product-dot]'));
      this.currentIndex = 0;
      this.frame = 0;
      this.slideOffsets = [];
      this.activated = false;

      if (!this.track || !this.slides.length) return;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPrevious = () => this.pageBy(-1);
      this.onNext = () => this.pageBy(1);
      this.onDotClick = this.onDotClick.bind(this);

      // Product rails are normally below the initial viewport. Keep their
      // ResizeObserver, scroll listeners and layout measurements dormant until
      // the customer approaches the section. Theme Editor remains immediate.
      if ('IntersectionObserver' in window && !window.Shopify?.designMode) {
        this.activationObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.activationObserver?.disconnect();
            this.activationObserver = null;
            this.activate();
          },
          { rootMargin: '600px 0px' }
        );
        this.activationObserver.observe(this);
      } else {
        this.activate();
      }
    }

    activate() {
      if (this.activated || !this.track) return;
      this.activated = true;

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('keydown', this.onKeydown);
      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);
      this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.onResize, { passive: true });
      }

      this.syncDesktopColumns();
      requestAnimationFrame(() => {
        if (!this.isConnected || !this.activated) return;
        this.refreshSlideOffsets();
        this.update();
      });
    }

    disconnectedCallback() {
      this.activationObserver?.disconnect();
      this.activationObserver = null;

      if (this.activated) {
        this.track?.removeEventListener('scroll', this.onScroll);
        this.track?.removeEventListener('keydown', this.onKeydown);
        this.previousButton?.removeEventListener('click', this.onPrevious);
        this.nextButton?.removeEventListener('click', this.onNext);
        this.dots?.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
        this.resizeObserver?.disconnect();
        window.removeEventListener('resize', this.onResize);
      }

      cancelAnimationFrame(this.frame);
      this.activated = false;
    }

    reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    configuredDesktopColumns() {
      return Math.max(1, Number(this.dataset.desktopColumns) || 4);
    }

    desktopVisibleCount() {
      const configured = this.configuredDesktopColumns();
      const availableWidth = this.clientWidth || this.track?.clientWidth || window.innerWidth;

      // Batch 1C: five is the maximum premium desktop density.
      // Larger shells increase card presence instead of adding a sixth card.
      if (availableWidth >= 1780) return Math.min(this.slides.length, Math.max(configured, 5));
      return Math.min(this.slides.length, configured);
    }

    syncDesktopColumns() {
      const columns = window.innerWidth >= 1200
        ? this.desktopVisibleCount()
        : this.configuredDesktopColumns();
      this.style.setProperty('--gvs-commerce-effective-columns', columns);
    }

    visibleCount() {
      const width = window.innerWidth;
      if (width < 750) {
        const mobileCardWidth = Number(this.dataset.mobileCardWidth) || 86;
        return mobileCardWidth <= 52 ? 2 : 1;
      }
      if (width < 1200) return Math.max(1, Number(this.dataset.tabletColumns) || 2);
      return this.desktopVisibleCount();
    }

    maximumStartIndex() {
      return Math.max(0, this.slides.length - this.visibleCount());
    }

    clampStartIndex(index) {
      return Math.max(0, Math.min(Number(index) || 0, this.maximumStartIndex()));
    }

    refreshSlideOffsets() {
      const firstSlide = this.slides[0];
      if (!firstSlide) {
        this.slideOffsets = [];
        return;
      }

      const firstOffset = firstSlide.offsetLeft;
      this.slideOffsets = this.slides.map((slide) => Math.max(0, slide.offsetLeft - firstOffset));
    }

    slideScrollLeft(index) {
      const targetIndex = this.clampStartIndex(index);
      const cachedOffset = this.slideOffsets[targetIndex];
      if (Number.isFinite(cachedOffset)) return cachedOffset;

      const firstSlide = this.slides[0];
      const slide = this.slides[targetIndex];
      if (!firstSlide || !slide) return 0;
      return Math.max(0, slide.offsetLeft - firstSlide.offsetLeft);
    }

    currentFromScroll() {
      const currentScroll = this.track.scrollLeft;
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(this.slideScrollLeft(index) - currentScroll);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      return this.clampStartIndex(nearestIndex);
    }

    goTo(index, behavior = this.reducedMotion() ? 'auto' : 'smooth') {
      const targetIndex = this.clampStartIndex(index);
      this.currentIndex = targetIndex;
      this.track.scrollTo({ left: this.slideScrollLeft(targetIndex), behavior });

      // Update immediately for responsive controls, then confirm after scrolling.
      this.updateControls(targetIndex);
    }

    pageBy(direction) {
      const current = this.currentFromScroll();
      const pageSize = this.visibleCount();
      const maximum = this.maximumStartIndex();
      const target = direction > 0
        ? Math.min(maximum, current + pageSize)
        : Math.max(0, current - pageSize);

      this.goTo(target);
    }

    onDotClick(event) {
      this.goTo(Number(event.currentTarget.dataset.gvsProductDot) || 0);
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.pageBy(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.pageBy(1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goTo(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goTo(this.maximumStartIndex());
      }
    }

    onScroll() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    }

    onResize() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        this.syncDesktopColumns();
        this.frame = requestAnimationFrame(() => {
          this.refreshSlideOffsets();
          const validIndex = this.clampStartIndex(this.currentIndex);
          this.goTo(validIndex, 'auto');
          this.update();
        });
      });
    }

    updateControls(index) {
      const maximum = this.maximumStartIndex();
      const hasOverflow = this.track.scrollWidth - this.track.clientWidth > 3;
      const atStart = index <= 0;
      const atEnd = index >= maximum;

      if (this.previousButton) {
        this.previousButton.disabled = !hasOverflow || atStart;
        this.previousButton.hidden = !hasOverflow;
      }

      if (this.nextButton) {
        this.nextButton.disabled = !hasOverflow || atEnd;
        this.nextButton.hidden = !hasOverflow;
      }

      this.dots.forEach((dot, dotIndex) => {
        const isDistinctPosition = dotIndex <= maximum;
        dot.hidden = !isDistinctPosition;
        dot.setAttribute('aria-hidden', isDistinctPosition ? 'false' : 'true');
        dot.tabIndex = isDistinctPosition ? 0 : -1;

        if (isDistinctPosition && dotIndex === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    update() {
      if (!this.track) return;
      this.currentIndex = this.currentFromScroll();
      this.updateControls(this.currentIndex);
    }
  }

  customElements.define('gvs-product-list-carousel', GVSProductListCarousel);
})();
